import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoIosAddCircleOutline } from 'react-icons/io';
import { MdBusinessCenter } from 'react-icons/md';
import { useContextSelector } from 'use-context-selector';

import { getPortalVisitList } from '@/core/apis/bentoboard-apis';
import { cacheSetAction } from '@/core/store/cache/cache-reducers';
import { CacheContext, setCacheState } from '@/core/store/cache/CacheProvider';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { usePagination } from '@/core/utils/usePagination';
import { appSettings } from '@/settings/app-settings';
import { Board } from '@/ux-legacy/components/common/bentoboard/Common/board-types';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

type PortalListingProps = {
  show: boolean;
  showNewPortalPopup: () => void;
  userId?: string;
};

const PortalListing: FC<PortalListingProps> = ({ show, showNewPortalPopup, userId }) => {
  const [portals, setPortals] = useState<any[]>([]);
  const [recentPortals, setRecentPortals] = useState<{ board: Board }[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const cacheDispatch = useContextSelector(CacheContext, setCacheState);
  const authentication = useContextSelector(AppContext, authSelector);

  const router = useRouter();

  const limit = 20;

  const fetchPotalVisitList = (search: string, limit: number, offset: number, existingPortals: Board[]) => {
    getPortalVisitList({ limit, offset, name: search })
      .then((res) => {
        setIsLoading(false);
        const newPortals = [...res.visitedPortals];
        setPortals([...existingPortals, ...newPortals]);
        setCurrentCount([...existingPortals, ...newPortals].length);
        setHasMore(offset < res.total);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  // const fetchPotalVisitList = () => {
  //   getPortalVisitList({ limit: 5, offset: 0 }).then((res) => {
  //     setRecentPortals(res.visitedPortals);
  //   });
  // };

  function loadMore() {
    setIsLoading(true);
    fetchPotalVisitList(searchQuery, limit, limit + offset, portals);
    setCurrentOffset(limit + offset);
  }

  const debouncedSearchPortals = useCallback(debounce(fetchPotalVisitList, 500), []);
  const { count, offset, moreElement, setCurrentCount, setCurrentOffset } = usePagination(loadMore, 0);

  useEffect(() => {
    if (show) {
      setPortals([]);
      setCurrentCount(0);
      setCurrentOffset(0);
      setHasMore(true);
      setIsLoading(true);
      const search = searchQuery[0] == '@' ? searchQuery.slice(1) : searchQuery;
      debouncedSearchPortals(search, limit, 0, []);
    }
  }, [searchQuery, show]);

  // useEffect(() => {
  //   fetchPotalVisitList();
  // }, []);

  return (
    <>
      {show && (
        <>
          <div className="my-2 flex justify-between gap-x-2">
            <div className="flex grow relative">
              <FaSearch className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 translate-x-2" />
              <input
                placeholder="Search Portals"
                className="w-full pr-4 pl-8 py-2 outline-none border-none bg-gray-200 rounded-full"
                value={searchQuery}
                autoComplete="off"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              ></input>
            </div>
            <button
              className="inline-flex items-center text-white justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 shadow h-11 px-4 py-2 bg-black"
              onClick={() => {
                showNewPortalPopup();
              }}
            >
              <IoIosAddCircleOutline className="h-6 w-6 md:h-4 md:w-4" />
              <span className="hidden md:inline md:ml-2">Create Portals</span>
            </button>
          </div>
          <div className=" overflow-y-auto">
            {/* {portals.length > 0 && <span className="bg-slate-200 p-1 font-bold text-slate-500">Recent Visits</span>} */}
            <div className="gap-2 flex flex-col">
              {portals.map((portal, index) => {
                return (
                  <button
                    key={portal._id}
                    className="w-full flex items-center gap-x-2 p-1 hover:bg-slate-100 rounded-md"
                    onClick={() => {
                      if (userId) {
                        cacheDispatch(
                          cacheSetAction({
                            [`SetProfilePageOpen_${userId}`]: undefined,
                          }),
                        );
                      }
                      router.push(`/portal?id=${portal?.board?._id}`);
                    }}
                  >
                    <img
                      src={
                        portal?.board.displayImage
                          ? appSettings.api.S3_MEDIA_COMPRESSED + portal?.board.displayImage
                          : IMG_PLACEHOLDER
                      }
                      className="w-12 h-12 rounded-full object-cover shrink-0"
                      alt=""
                    ></img>
                    {/* <span className="w-full text-start truncate">{portal?.board.name || 'Untitled'}</span> */}
                    {portal?.board?.type !== 'BUSINESS' && (
                      <>
                        {portal?.board ? (
                          <div className="w-full text-start truncate">
                            <span className=" font-bold w-full truncate text-[18px]">
                              {portal?.board?.name || 'Untitled'}
                            </span>
                            {portal.board?.createdUserInfo && (
                              <div className="flex items-center ">
                                {'From '}
                                {authentication.userId == portal.board.createdBy
                                  ? 'You'
                                  : portal.board?.createdUserInfo?.Name ||
                                    portal.board?.createdUserInfo?.Email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-full text-start truncate">
                            <span className=" font-bold w-full truncate text-[18px]">
                              {portal?.name || 'Untitled'}
                            </span>
                            {portal.userInfo && (
                              <div className="flex items-center ">
                                {'From '}
                                {authentication.userId == portal?.createdBy
                                  ? 'You'
                                  : portal?.userInfo?.Name || portal?.userInfo?.Email}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {portal?.board?.type === 'BUSINESS' && (
                      <div className="w-full text-start truncate">
                        <span className=" font-bold w-full truncate text-[18px]">
                          {portal?.board?.name || portal?.name || 'Untitled'}
                        </span>
                        <div className="flex items-center ">
                          <MdBusinessCenter />
                          <span className=" truncate ml-1">
                            {portal?.board?.vendor?.businessName
                              ? portal?.board?.vendor?.businessName
                              : portal?.vendor?.businessName}
                          </span>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
              {!isLoading && hasMore && <div ref={moreElement} className="w-full h-[50px] flex"></div>}
              {isLoading && (
                <div className="w-full h-[100px] flex items-center justify-center">
                  <Loader />
                </div>
              )}
              {!isLoading && portals.length == 0 && (
                <div className="w-full h-[100px] flex items-center justify-center italic">
                  No Portals Found.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PortalListing;
